(ns client-portal.client.components.drill
  "The product-neutral half of a chart's `drill`: the constraints a double-clicked point
   carries and the report's own filter selections they layer over. Each product's
   sessions drill maps the constraints onto its own Sessions filters and route."
  (:require [cljs-time.core :as t]
            [cljs-time.format :as f]
            [clojure.string :as s]
            [client-portal.client.concepts.data :as concepts.d]
            [client-portal.client.core.data :as core.d]
            [client-portal.client.locations.data :as locations.d]
            [client-portal.client.smart-ordering.filters.views :as so.filters]
            [client-portal.client.voice-insights.data :as vi.d]
            [shared.core.constants :as constants]
            [shared.core.reactive :as re]
            [shared.core.router :as router]
            [shared.core.util :as util]))

(re/reg-sub
  :drill/context-
  (fn [db]
    {:concept-id (concepts.d/current-concept-id db)
     :location-id (locations.d/current-selected-location-id db)
     :crew-type (core.d/selected-crew-type db)
     :timeframe (core.d/selected-timeframe db)
     :timeframe-date-range (core.d/selected-timeframe-date-range db)
     :date-window (core.d/timeframe-date-params db)
     :day-part (let [day-part (vi.d/selected-day-part db)]
                 (when (not= day-part (:label vi.d/day-part-all-day))
                   day-part))
     :query-params (router/current-query-params db)}))

(defn value-set? [v]
  (and (some? v) (not (s/blank? (str v)))))

(defn report-filter-enabled?
  "Whether the report carries its selection for filter `k` over — every filter does
   outside a report."
  [report-filters k]
  (get report-filters k true))

(defn option
  "The value a Sessions filter dropdown stores for an option, so the filter opens with it
   picked."
  [display-name value]
  (util/->kebab-case-string {:name display-name :value value}))

(defn day-of-week-option [v]
  (let [v-str (str v)]
    (when-let [day (if (re-matches #"\d" v-str)
                     ;; BigQuery's DAYOFWEEK, 1 = Sunday
                     (->> (util/parse-int v-str) (dec) (get constants/days-of-the-week))
                     (when (>= (count v-str) 3)
                       (some #(when (s/starts-with? (s/lower-case %) (s/lower-case v-str)) %)
                             constants/days-of-the-week)))]
      (option day day))))

(defn time-of-day-option [v]
  (let [v-str (str v)]
    (when-let [hour (if (re-matches #"\d{1,2}" v-str)
                      (get constants/hours-of-day (util/parse-int v-str))
                      (let [normalized (-> v-str
                                           (s/lower-case)
                                           (s/replace #"\s" "")
                                           (s/replace #"^0(?=\d)" "")
                                           (s/replace #"^(\d+)(am|pm)$" "$1:00$2"))]
                        (some #(when (= % normalized) %) constants/hours-of-day)))]
      (option hour (util/time-of-day-with-leading-zero hour)))))

(defn duration-option [v]
  (some->> so.filters/duration-options
           (some #(when (= (:name %) (str v)) %))
           (util/->kebab-case-string)))

(defn time-period-constraints
  "The constraints a point carries on a chart showing the time-period toggle: its x value
   read as the selected period — the raw value the row kept before its label was
   formatted, else the x value itself. A shift is `<date> <day part>`."
  [selected-time-period x raw-time-value]
  (let [raw (str (or raw-time-value x))
        idx (some-> raw (s/index-of " "))]
    (case selected-time-period
      "daily" [{:filter "date" :values [raw]}]
      "hourly" [{:filter "time-of-day" :values [raw]}]
      "day-of-week" [{:filter "day-of-week" :values [raw]}]
      "day-part" [{:filter "day-part" :values [raw]}]
      "shift" [{:filter "date" :values [(if idx (subs raw 0 idx) raw)]}
               {:filter "day-part" :values [(when idx (subs raw (inc idx)))]}]
      nil)))

(defn constraints
  "What a double-clicked point narrows Sessions by, lowest precedence first: the report's
   selected day part when it applies one and the point brings none of its own, the
   `drill` spec's fixed `filters`, the point's flagged `columns`, the clicked series' key
   with `series`, then any the chart adds itself (`extra`, e.g. its time-period toggle). A
   tag can be read off another column of the row (`tag-column`)."
  [{:keys [columns series filters]} {:keys [row series-key extra]} {:keys [day-part]} report-filters]
  (let [row-get (fn [column]
                  (->> [column (keyword column) (util/->kebab-case-keyword column)]
                       (map #(get row %))
                       (filter some?)
                       (first)))
        with-tag (fn [{:keys [tag-column] :as c}]
                   (cond-> c tag-column (assoc :tag (row-get tag-column))))
        point (concat filters
                      (keep (fn [{:keys [column] :as c}]
                              (let [v (row-get column)]
                                (when (value-set? v)
                                  (-> c (with-tag) (assoc :values [v])))))
                            columns)
                      (when (and series (value-set? series-key))
                        [(-> series (with-tag) (assoc :values [series-key]))])
                      extra)]
    (cond->> point
             (and day-part
                  (report-filter-enabled? report-filters :day-part)
                  (not-any? #(= "day-part" (:filter %)) point))
             (cons {:filter "day-part" :values [day-part]}))))

(defn- date-params
  "The `grain` bucket a date value starts, clipped to the report's half-open date window,
   as the Sessions custom range (whose end is inclusive)."
  [v grain {:keys [start-date end-date]}]
  (when-let [day (re-find #"^\d{4}-\d{2}-\d{2}" (str v))]
    (let [fmt core.d/date-formatter-yyyy-MM-dd
          bucket-end (-> (f/parse fmt day)
                         (t/plus (case grain
                                   "week" (t/weeks 1)
                                   "month" (t/months 1)
                                   (t/days 1)))
                         (#(f/unparse fmt %)))
          start (if (pos? (compare start-date day)) start-date day)
          end (if (and end-date (neg? (compare end-date bucket-end))) end-date bucket-end)]
      (when (neg? (compare start end))
        {:tf -1
         :start start
         :end (->> (t/minus (f/parse fmt end) (t/days 1))
                   (f/unparse fmt))}))))

(defn layout-query-params
  "The concept, location and date Sessions opens with: the point's own values where it has
   them, else the report's current selections, but only for the filters the report
   enables."
  [{:keys [concept-id location-id timeframe timeframe-date-range date-window]} constraints report-filters]
  (let [date-enabled? (report-filter-enabled? report-filters :date)
        by-filter (group-by :filter constraints)
        layout-value #(some->> (get by-filter %) (last) :values (filter value-set?) (first))]
    (util/remove-nil-from-map
      (merge {:cid concept-id}
             (when (report-filter-enabled? report-filters :location)
               {:lid location-id})
             (when date-enabled?
               (cond-> {:tf timeframe}
                       (and (= timeframe -1) (not-empty timeframe-date-range))
                       (assoc :start (core.d/format-start-end-date (first timeframe-date-range))
                              :end (core.d/format-start-end-date (second timeframe-date-range)))))
             (some->> (layout-value "concept") (hash-map :cid))
             (some->> (layout-value "location") (hash-map :lid))
             (some (fn [{:keys [values grain]}]
                     (date-params (first values) grain (when date-enabled? date-window)))
                   (reverse (get by-filter "date")))))))
